/**
 * Workspace File Browser Component
 * Dialog-based file explorer for browsing, previewing, and uploading files
 * in an agent's workspace execution environment.
 *
 * On open, resolves the workspace sandboxId from the chat record.
 * If no workspace is found, shows a message instead of erroring.
 *
 * @module components/chat/file-browser/file-browser
 */

import { Button } from "@/components/ui/button";
import { FilePreviewBody, urlSource } from "@/components/file-preview";
import type { FileKind, FileSource as PreviewFileSource } from "@/components/file-preview";
import { SpreadsheetGrid } from "@/components/artifacts/spreadsheet-grid";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import type { ParsedCsv } from "@/lib/artifacts/csv";
import { cn } from "@/lib/utils";
import { getAuthHeader } from "@/utils";
import {
  Archive,
  ChevronLeft,
  ChevronRight,
  Coffee,
  Download,
  File,
  FileCode,
  FileSpreadsheet,
  FileText,
  Folder,
  Home,
  Image,
  Music,
  RefreshCw,
  Upload,
  Video,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

/** A workbook sheet parsed into the shared spreadsheet-grid contract. */
interface WorkbookSheetPreview {
  name: string;
  parsed: ParsedCsv;
  visibleRows: number;
  totalRows: number;
}

/** Max rows rendered per sheet in the preview (download for the full file). */
const SHEET_PREVIEW_MAX_ROWS = 200;

/**
 * Text / code extensions that render through the shared `CodeSourceRenderer`
 * (syntax-highlighted, read-only) instead of a raw `<pre>`. Mirrors the
 * file-browser's `textExtensions` minus the types that already have a more
 * specific shared renderer (markdown, html, csv) or native handling
 * (audio/video/docx/xlsx). `tsv` stays here rather than `spreadsheet` — the
 * shared grid parses comma-separated CSV, so a tab file would mis-column.
 */
const TEXT_CODE_EXTENSIONS = new Set([
  "txt", "rst", "json", "jsonl", "ndjson", "js", "jsx", "ts", "tsx", "mjs",
  "cjs", "css", "scss", "sass", "less", "xml", "yaml", "yml", "py", "java",
  "kt", "swift", "scala", "dart", "lua", "r", "pl", "cpp", "c", "h", "hpp",
  "cs", "php", "rb", "go", "rs", "sh", "ps1", "bat", "cmd", "sql", "tsv",
  "env", "log", "toml", "ini", "cfg", "conf", "properties", "dockerfile",
  "makefile", "cmake", "gradle", "graphql", "gql", "proto", "tf", "hcl",
  "vue", "svelte", "astro",
]);

/** Build a file-preview kind for file types that can share the unified renderers. */
function previewKindForExtension(extension: string): FileKind | null {
  if (extension === "pdf") return "pdf";
  if (["jpg", "jpeg", "png", "gif", "svg", "webp", "bmp"].includes(extension)) {
    return "image";
  }
  if (["mp3", "wav", "m4a", "ogg", "flac", "aac"].includes(extension)) {
    return "audio";
  }
  if (["mp4", "webm", "mov", "m4v"].includes(extension)) return "video";
  if (extension === "docx") return "docx";
  if (extension === "html" || extension === "htm") return "html";
  if (extension === "md" || extension === "markdown") return "markdown";
  if (extension === "csv") return "spreadsheet";
  if (TEXT_CODE_EXTENSIONS.has(extension)) return "code";
  return null;
}

/** MIME hints keep blob previews renderable when the workspace route is generic. */
function contentTypeForExtension(extension: string): string | undefined {
  switch (extension) {
    case "pdf":
      return "application/pdf";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "svg":
      return "image/svg+xml";
    case "webp":
      return "image/webp";
    case "bmp":
      return "image/bmp";
    case "html":
    case "htm":
      return "text/html";
    case "md":
    case "markdown":
      return "text/markdown";
    case "csv":
      return "text/csv";
    case "mp3":
      return "audio/mpeg";
    case "wav":
      return "audio/wav";
    case "m4a":
      return "audio/mp4";
    case "ogg":
      return "audio/ogg";
    case "flac":
      return "audio/flac";
    case "aac":
      return "audio/aac";
    case "mp4":
    case "m4v":
      return "video/mp4";
    case "webm":
      return "video/webm";
    case "mov":
      return "video/quicktime";
    case "docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    default:
      return undefined;
  }
}

/** Convert SheetJS row arrays into the shared spreadsheet-grid shape. */
function rowsToParsedCsv(rows: string[][]): ParsedCsv {
  if (rows.length === 0) return { headers: [], rows: [], cellCount: 0 };
  const [headers = [], ...dataRows] = rows;
  let cellCount = headers.length;
  for (const row of dataRows) cellCount += row.length;
  return { headers, rows: dataRows, cellCount };
}

/**
 * Renders parsed workbook sheets with the shared spreadsheet grid. SheetJS
 * handles binary XLS/XLSX/ODS parsing; the visual table stays the same as
 * spreadsheet artifacts and Library previews.
 */
function WorkbookSpreadsheetPreview({ sheets }: { sheets: WorkbookSheetPreview[] }) {
  if (sheets.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground italic">
        No data
      </div>
    );
  }

  if (sheets.length === 1) {
    const sheet = sheets[0];
    return (
      <div className="relative h-full">
        <SpreadsheetGrid parsed={sheet.parsed} className="absolute inset-0 bg-muted/10" />
        {sheet.totalRows > sheet.visibleRows ? (
          <div className="pointer-events-none absolute bottom-2 right-3 rounded border bg-background/90 px-2 py-1 text-xs text-muted-foreground shadow-sm">
            Showing first {sheet.visibleRows} of {sheet.totalRows} rows
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-muted/10 p-4">
      <div className="space-y-4">
        {sheets.map((sheet) => (
          <section key={sheet.name} className="overflow-hidden rounded border bg-background">
            <div className="flex items-center justify-between border-b bg-muted px-3 py-2">
              <div className="truncate text-xs font-semibold text-muted-foreground">
                {sheet.name}
              </div>
              {sheet.totalRows > sheet.visibleRows ? (
                <div className="text-xs text-muted-foreground">
                  Showing first {sheet.visibleRows} of {sheet.totalRows} rows
                </div>
              ) : null}
            </div>
            <SpreadsheetGrid parsed={sheet.parsed} className="h-80 bg-muted/10" />
          </section>
        ))}
      </div>
    </div>
  );
}

/**
 * Returns the appropriate icon component based on file extension
 */
const getFileIcon = (fileName: string, isDir: boolean) => {
  if (isDir) return Folder;

  const extension = fileName.split(".").pop()?.toLowerCase() || "";

  if (["jpg", "jpeg", "png", "gif", "svg", "webp", "bmp"].includes(extension)) return Image;
  if (["js", "jsx", "ts", "tsx", "html", "css", "json", "xml", "yaml", "yml", "py", "java", "cpp", "c", "h", "php", "rb", "go", "rs", "sh"].includes(extension)) return FileCode;
  if (["csv", "xlsx", "xls", "ods"].includes(extension)) return FileSpreadsheet;
  if (["zip", "rar", "tar", "gz", "7z", "bz2"].includes(extension)) return Archive;
  if (["mp3", "wav", "flac", "aac", "ogg", "m4a"].includes(extension)) return Music;
  if (["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv"].includes(extension)) return Video;
  if (["txt", "md", "rst", "log"].includes(extension)) return FileText;

  return File;
};

/** File entry shape from the workspace file API */
interface WorkspaceFileEntry {
  name: string;
  path: string;
  isDirectory: boolean;
  size?: number;
  modified?: string;
  /** DO Spaces storage key for archived files */
  storageKey?: string;
}

/** Source of file data: live local workspace, live Daytona sandbox, S3 sync mirror, or archived snapshot */
type FileSource = "local" | "daytona" | "sync" | "snapshot";

interface FileBrowserProps {
  /** Chat ID used to resolve the workspace sandboxId on open */
  chatId: string;
  /** Agent stream status used to refresh generated files after a run finishes */
  agentStatus?: string;
  /** Callback when a file is selected for insertion */
  onSelectFile?: (path: string, content: string) => void;
  /** Trigger element rendered as the dialog trigger */
  children?: React.ReactNode;
  /** Controlled open state */
  open?: boolean;
  /** Controlled open state change handler */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Workspace file browser dialog.
 * Resolves the workspace from the chat record on open,
 * then connects to workspace-files API for file operations.
 */
export function FileBrowser({
  chatId,
  agentStatus,
  onSelectFile,
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: FileBrowserProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const previousAgentStatus = useRef<string | undefined>(agentStatus);

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = controlledOnOpenChange || setInternalOpen;

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

  /**
   * Workspace files are resolved by chatId on the server (no more
   * `chat.sandboxId → sandbox_sessions` indirection). Each chat has its
   * own `/tmp/taqat-env/agent-{8chars}` directory, so the listing is
   * automatically chat-scoped.
   */
  const isWorkspaceResolved = !!chatId;
  /** Error message when listing fails or the chat has no workspace yet */
  const [resolveError, setResolveError] = useState<string | null>(null);

  const [currentPath, setCurrentPath] = useState("/workspace");
  const [files, setFiles] = useState<WorkspaceFileEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [sheetPreview, setSheetPreview] = useState<WorkbookSheetPreview[] | null>(null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<"list" | "preview">("list");
  const [isUploading, setIsUploading] = useState(false);
  /** Whether files are from live workspace or archived snapshot */
  const [fileSource, setFileSource] = useState<FileSource>("local");

  const fileInputRef = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    return input;
  }, []);

  /** Load files from the workspace at the given path */
  const loadFiles = useCallback(
    async (path: string) => {
      if (!chatId) return;
      // Clear stale entries up-front so navigating between folders never flashes
      // the previous directory's contents while the new listing is in flight.
      setFiles([]);
      setIsLoading(true);
      try {
        const authHeader = getAuthHeader();
        const response = await fetch(
          `${API_BASE_URL}/workspace-files/chat/${chatId}/files?path=${encodeURIComponent(path)}`,
          {
            headers: authHeader ? { Authorization: authHeader } : {},
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to load files: ${response.status}`);
        }

        const data = await response.json();
        const source: FileSource = data?.data?.source || "local";
        setFileSource(source);

        // Normalize API response: route returns { type: 'file' | 'directory' }
        // but component uses isDirectory boolean
        const rawEntries = data?.data?.files || [];
        const entries: WorkspaceFileEntry[] = rawEntries.map(
          (entry: { name: string; path: string; type?: string; isDirectory?: boolean; size?: number; storageKey?: string }) => ({
            name: entry.name,
            path: entry.path,
            isDirectory: entry.isDirectory ?? entry.type === "directory",
            size: entry.size,
            storageKey: entry.storageKey,
          })
        );

        setFiles(entries);
        setCurrentPath(path);

        // Build breadcrumbs from path (strip /workspace prefix for display)
        const displayPath = path.replace(/^\/workspace\/?/, "");
        if (!displayPath) {
          setBreadcrumbs([]);
        } else {
          setBreadcrumbs(displayPath.split("/").filter(Boolean));
        }
      } catch (error) {
        toast.error("Failed to load files");
        console.error("Failed to load files:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [chatId, API_BASE_URL]
  );

  /** On dialog open: load files directly — workspace resolution happens server-side. */
  useEffect(() => {
    if (isOpen) {
      setResolveError(null);
      setFiles([]);
      setFileContent(null);
      setSheetPreview(null);
      setSelectedFile(null);
      setViewMode("list");
      if (chatId) {
        loadFiles("/workspace");
      } else {
        setResolveError("No chat ID available");
      }
    }
  }, [isOpen, chatId, loadFiles]);

  /**
   * Refresh the visible listing when an agent run finishes while the file
   * browser is already open. This covers multi-file generation workflows where
   * users keep the explorer open and expect new files to appear immediately.
   */
  useEffect(() => {
    const previous = previousAgentStatus.current;
    previousAgentStatus.current = agentStatus;

    const wasGenerating = previous === "submitted" || previous === "streaming";
    if (!isOpen || !chatId || !wasGenerating || agentStatus !== "ready") return;

    loadFiles(currentPath);
  }, [agentStatus, chatId, currentPath, isOpen, loadFiles]);

  /** File extensions that can be previewed as text */
  const textExtensions = new Set([
    "txt", "md", "markdown", "rst", "json", "jsonl", "ndjson", "js", "jsx",
    "ts", "tsx", "mjs", "cjs", "html", "css", "scss", "sass", "less", "xml",
    "yaml", "yml", "py", "java", "kt", "swift", "scala", "dart", "lua", "r",
    "pl", "cpp", "c", "h", "hpp", "cs", "php", "rb", "go", "rs", "sh", "ps1",
    "bat", "cmd", "sql", "csv", "tsv", "env", "log", "toml", "ini", "cfg",
    "conf", "properties", "dockerfile", "makefile", "cmake", "gradle",
    "graphql", "gql", "proto", "tf", "hcl", "vue", "svelte", "astro",
  ]);

  /**
   * Spreadsheet formats parsed with SheetJS into the shared grid. Everything
   * else previewable (pdf, image, audio, video, html, markdown, csv, docx,
   * code) is classified by `previewKindForExtension` and rendered by the
   * shared `FilePreviewBody`.
   */
  const spreadsheetExtensions = new Set(["xlsx", "xls", "ods"]);
  /**
   * Mint a short-lived single-file access token (Bearer-authenticated) so the
   * content URL can be loaded via `<iframe>`/`<img>` `src` or `window.open`,
   * none of which can carry an Authorization header. Returns null on failure.
   */
  const fetchContentToken = useCallback(
    async (filePath: string): Promise<string | null> => {
      if (!chatId) return null;
      try {
        const authHeader = getAuthHeader();
        const res = await fetch(
          `${API_BASE_URL}/workspace-files/chat/${chatId}/files/content-token?path=${encodeURIComponent(filePath)}`,
          { headers: authHeader ? { Authorization: authHeader } : {} }
        );
        if (!res.ok) return null;
        const data = await res.json();
        return data?.data?.token ?? null;
      } catch {
        return null;
      }
    },
    [chatId, API_BASE_URL]
  );

  /** Build the workspace content URL for a file. */
  const getRawContentUrl = useCallback(
    (filePath: string) =>
      `${API_BASE_URL}/workspace-files/chat/${chatId}/files/content?path=${encodeURIComponent(filePath)}`,
    [API_BASE_URL, chatId],
  );

  /**
   * Source adapter for file types that can share the unified file-preview
   * renderers used by Library and artifact surfaces.
   */
  const selectedPreviewSource = useMemo<PreviewFileSource | null>(() => {
    if (!selectedFile || !chatId) return null;
    const extension = selectedFile.split(".").pop()?.toLowerCase() || "";
    const kind = previewKindForExtension(extension);
    if (!kind) return null;
    const url = getRawContentUrl(selectedFile);
    const filename = selectedFile.split("/").pop() || selectedFile;
    return urlSource({
      url,
      filename,
      kind,
      contentType: contentTypeForExtension(extension),
      authRequired: true,
      resolveTokenizedUrl: async () => {
        const token = await fetchContentToken(selectedFile);
        return token ? `${url}&token=${encodeURIComponent(token)}` : url;
      },
    });
  }, [chatId, fetchContentToken, getRawContentUrl, selectedFile]);

  /** Load file content for preview */
  const loadFileContent = async (path: string) => {
    if (!chatId) return;
    setIsLoading(true);
    setSelectedFile(path);
    try {
      const ext = path.split(".").pop()?.toLowerCase() || "";
      const isTextPreviewable = textExtensions.has(ext);

      // Shared file-preview renderers own these kinds (pdf, image, audio,
      // video, html, markdown, csv, docx, code). `FilePreviewBody` fetches +
      // renders via the source adapter (`selectedPreviewSource`), which
      // authenticates with the bearer header — so we don't fetch anything or
      // mint a token here; the renderer's blob/text accessor does it.
      if (previewKindForExtension(ext)) {
        setSheetPreview(null);
        setFileContent(null);
        setViewMode("preview");
        setIsLoading(false);
        return;
      }

      // Spreadsheets: fetch the bytes and parse with SheetJS so they render as
      // a table instead of the "binary — cannot preview" fallback.
      if (spreadsheetExtensions.has(ext)) {
        try {
          const authHeader = getAuthHeader();
          const res = await fetch(
            `${API_BASE_URL}/workspace-files/chat/${chatId}/files/content?path=${encodeURIComponent(path)}`,
            { headers: authHeader ? { Authorization: authHeader } : {} }
          );
          if (!res.ok) throw new Error(`content ${res.status}`);
          const buf = await res.arrayBuffer();
          const wb = XLSX.read(buf, { type: "array" });
          const sheets: WorkbookSheetPreview[] = wb.SheetNames.map((name) => {
            const rows = (
              XLSX.utils.sheet_to_json(wb.Sheets[name], {
                header: 1,
                blankrows: false,
                defval: "",
              }) as unknown[][]
            ).map((row) => row.map((cell) => String(cell ?? "")));
            const visibleRows = rows.slice(0, SHEET_PREVIEW_MAX_ROWS);
            return {
              name,
              parsed: rowsToParsedCsv(visibleRows),
              visibleRows: visibleRows.length,
              totalRows: rows.length,
            };
          });
          setSheetPreview(sheets);
          setFileContent("__sheet__preview__");
        } catch (err) {
          console.error("Failed to parse spreadsheet:", err);
          setSheetPreview(null);
          setFileContent("[Could not read spreadsheet — use the download button.]");
        }
        setViewMode("preview");
        setIsLoading(false);
        return;
      }

      if (!isTextPreviewable) {
        setSheetPreview(null);
        setFileContent("[Binary file - cannot preview. Use the download button.]");
        setViewMode("preview");
        setIsLoading(false);
        return;
      }

      const authHeader = getAuthHeader();
      // The route returns raw file content (text or 302 redirect for archived files)
      const response = await fetch(
        `${API_BASE_URL}/workspace-files/chat/${chatId}/files/content?path=${encodeURIComponent(path)}`,
        {
          headers: authHeader ? { Authorization: authHeader } : {},
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to load file content:", errorText);
        toast.error("Failed to load file content");
        setFileContent(`[Error loading file: ${response.status} ${response.statusText}]`);
        return;
      }

      // Route returns raw content with Content-Type header
      const contentType = response.headers.get("content-type") || "";

      setSheetPreview(null);

      if (contentType.startsWith("application/json") && !contentType.includes("text")) {
        // Check if this is an error JSON response
        const json = await response.json();
        if (json?.success === false) {
          setFileContent(`[Error: ${json.error || "Unknown error"}]`);
          return;
        }
        // It's actual JSON file content
        setFileContent(JSON.stringify(json, null, 2));
      } else {
        // Text content — read as text
        const content = await response.text();

        if (ext === "json") {
          try {
            setFileContent(JSON.stringify(JSON.parse(content), null, 2));
          } catch {
            setFileContent(content);
          }
        } else {
          setFileContent(content);
        }
      }
    } catch (error) {
      toast.error("Failed to load file content");
      console.error("Failed to load file content:", error);
      setFileContent(null);
    } finally {
      setIsLoading(false);
    }
  };

  /** Handle file or folder click */
  const handleItemClick = (file: WorkspaceFileEntry) => {
    if (file.isDirectory) {
      loadFiles(file.path);
    } else {
      loadFileContent(file.path);
      setViewMode("preview");
    }
  };

  /** Handle back to file list */
  const handleBackToList = () => {
    setViewMode("list");
    setSelectedFile(null);
    setFileContent(null);
    setSheetPreview(null);
  };

  /** Handle file upload via workspace API */
  const handleUpload = () => {
    if (!chatId) return;
    const input = fileInputRef();
    input.onchange = async (e) => {
      const uploadFiles = (e.target as HTMLInputElement).files;
      if (!uploadFiles || uploadFiles.length === 0) return;

      setIsUploading(true);
      try {
        const authHeader = getAuthHeader();
        for (const file of Array.from(uploadFiles)) {
          const isTextFile = file.type.startsWith("text/") ||
            ["application/json", "application/javascript", "application/xml"].includes(file.type);

          const content = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            if (isTextFile) {
              reader.onload = () => resolve(reader.result as string);
              reader.readAsText(file);
            } else {
              reader.onload = () => {
                const base64 = (reader.result as string).split(",")[1];
                resolve(base64);
              };
              reader.readAsDataURL(file);
            }
          });

          const filePath = currentPath === "/workspace"
            ? `/workspace/${file.name}`
            : `${currentPath}/${file.name}`;

          const response = await fetch(
            `${API_BASE_URL}/workspace-files/chat/${chatId}/files`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...(authHeader && { Authorization: authHeader }),
              },
              body: JSON.stringify({
                path: filePath,
                content,
                encoding: isTextFile ? "utf-8" : "base64",
              }),
            }
          );

          if (!response.ok) {
            throw new Error(`Failed to upload ${file.name}`);
          }
        }

        toast.success(`Uploaded ${uploadFiles.length} file(s)`);
        loadFiles(currentPath);
      } catch (error) {
        toast.error("Failed to upload files");
        console.error("Upload error:", error);
      } finally {
        setIsUploading(false);
      }
    };
    input.click();
  };

  /** Handle file download */
  const handleDownload = async () => {
    if (!chatId || !selectedFile) {
      toast.info("Select a file to download");
      return;
    }

    // window.open can't send a Bearer header, so mint a short-lived single-file
    // token and append it (the content route accepts ?token= for GETs). The
    // download=true param sets Content-Disposition: attachment server-side.
    const token = await fetchContentToken(selectedFile);
    const base = `${API_BASE_URL}/workspace-files/chat/${chatId}/files/content?path=${encodeURIComponent(selectedFile)}`;
    const directDownloadUrl = `${base}${token ? `&token=${encodeURIComponent(token)}` : ""}&download=true`;
    window.open(directDownloadUrl, "_blank");
  };

  /** Navigate to a specific breadcrumb level */
  const navigateToBreadcrumb = (index: number) => {
    // Exit preview mode first — otherwise loadFiles updates `files` underneath
    // the preview pane and the user just sees the same file open.
    if (viewMode === "preview") {
      setViewMode("list");
      setSelectedFile(null);
      setFileContent(null);
      setSheetPreview(null);
    }
    if (index === -1) {
      loadFiles("/workspace");
    } else {
      const path = "/workspace/" + breadcrumbs.slice(0, index + 1).join("/");
      loadFiles(path);
    }
  };

  /** Handle select button click */
  const handleSelectFile = () => {
    if (selectedFile && fileContent && onSelectFile) {
      onSelectFile(selectedFile, fileContent);
      setIsOpen(false);
    }
  };

  /** Whether the workspace is ready for file operations */
  const isWorkspaceReady = isWorkspaceResolved;
  /** Resolution is now synchronous (no chat-record fetch); kept for the loading branch below. */
  const isResolving = false;
  const isSharedPreview = selectedPreviewSource !== null;
  const isFullBleedPreview =
    isSharedPreview || fileContent === "__sheet__preview__";
  const isTallPreview = selectedPreviewSource?.kind === "pdf";

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {/* Trigger is optional: when opened from elsewhere (e.g. the mobile
          overflow menu) the dialog is driven purely by the controlled `open`
          prop and renders no inline trigger. */}
      {children ? (
        <DialogTrigger asChild>{children}</DialogTrigger>
      ) : null}
      <DialogContent className="flex h-[82dvh] max-h-[82dvh] w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden p-3 sm:h-auto sm:w-full sm:max-w-[900px] sm:p-6">
        {/* Header */}
        <DialogHeader className="flex-shrink-0 space-y-3">
          {/* `pr-8` reserves space for the dialog's absolute top-right close (X)
              button so the refresh control doesn't sit underneath it. */}
          <DialogTitle className="flex items-center justify-between pr-8">
            <div className="flex items-center gap-2">
              {viewMode === "preview" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToList}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              )}
              <span>Workspace Files</span>
              {viewMode === "list" && isWorkspaceReady && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground bg-muted rounded-md px-2 py-1">
                    {files.length} {files.length === 1 ? "item" : "items"}
                  </span>
                  {fileSource === "snapshot" && (
                    <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 rounded-md px-2 py-1 flex items-center gap-1">
                      <Archive className="h-3 w-3" />
                      Archived
                    </span>
                  )}
                </div>
              )}
            </div>
            {isWorkspaceReady && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => loadFiles(currentPath)}
                disabled={isLoading}
                title="Refresh file list"
                data-testid="workspace-file-refresh"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
              </Button>
            )}
          </DialogTitle>

          {/* Breadcrumbs — visible in both list and preview, so users know where they are while previewing. */}
          {isWorkspaceReady && (
            <div className="flex items-center gap-1 text-sm flex-wrap">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 py-1 px-2 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => navigateToBreadcrumb(-1)}
              >
                <Home className="mr-1 h-3 w-3" />
                <span className="text-sm">Workspace</span>
              </Button>
              {breadcrumbs.map((part, index) => (
                <div
                  key={`breadcrumb-${index}-${part}`}
                  className="flex items-center min-w-0"
                >
                  <ChevronRight className="mx-0.5 h-3 w-3 text-muted-foreground/60 flex-shrink-0" />
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`h-7 px-2 min-w-0 max-w-32 transition-colors ${
                      index === breadcrumbs.length - 1 && viewMode === "list"
                        ? "text-foreground font-medium"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    onClick={() => navigateToBreadcrumb(index)}
                    title={part}
                  >
                    <span className="truncate text-xs">{part}</span>
                  </Button>
                </div>
              ))}
              {viewMode === "preview" && selectedFile && (
                <div className="flex items-center min-w-0">
                  <ChevronRight className="mx-0.5 h-3 w-3 text-muted-foreground/60 flex-shrink-0" />
                  <span
                    className="truncate text-xs font-medium text-foreground px-2 max-w-48"
                    title={selectedFile.split("/").pop() || selectedFile}
                  >
                    {selectedFile.split("/").pop()}
                  </span>
                </div>
              )}
            </div>
          )}
        </DialogHeader>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden">
          {isResolving ? (
            /* Loading workspace */
            <div className="flex h-full sm:h-[500px] flex-col items-center justify-center text-muted-foreground">
              <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-muted border-t-primary" />
              <p className="text-lg font-medium">Connecting to workspace</p>
              <p className="text-sm">Please wait...</p>
            </div>
          ) : resolveError ? (
            /* Workspace not found */
            <div className="flex h-full sm:h-[500px] flex-col items-center justify-center px-8">
              <div className="flex flex-col items-center max-w-sm">
                <div className="rounded-full bg-orange-50 p-4 dark:bg-orange-950/30">
                  <Folder className="h-10 w-10 text-primary/60" />
                </div>
                <h3 className="mt-5 text-base font-semibold text-foreground">Workspace not available</h3>
                <p className="mt-2 text-sm text-muted-foreground text-center leading-relaxed">
                  {resolveError}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-5"
                  onClick={() => loadFiles("/workspace")}
                >
                  Try Again
                </Button>
              </div>
            </div>
          ) : viewMode === "list" ? (
            /* File Grid View */
            <div className="h-full sm:h-[500px] overflow-y-auto p-4">
              {isLoading && !files.length ? (
                <div className="grid grid-cols-3 gap-4 sm:grid-cols-6">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
                    <div key={i} className="flex flex-col items-center space-y-2">
                      <Skeleton className="h-12 w-12 rounded" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  ))}
                </div>
              ) : files.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
                  <Coffee className="mb-4 h-12 w-12 opacity-50" />
                  <p className="text-lg font-medium">No files found</p>
                  <p className="text-sm">This directory is empty</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4 sm:grid-cols-6">
                  {files.map((file, index) => {
                    const IconComponent = getFileIcon(file.name, file.isDirectory);
                    return (
                      <button
                        key={`${file.path}-${index}`}
                        className="flex flex-col items-center space-y-2 rounded-lg p-3 text-center transition-colors hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring"
                        onClick={() => handleItemClick(file)}
                        title={file.name}
                      >
                        <div
                          className={`rounded-lg p-2 ${
                            file.isDirectory
                              ? "bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400"
                              : "bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                          }`}
                        >
                          <IconComponent className="h-8 w-8" />
                        </div>
                        <span className="max-w-full truncate text-xs font-medium">
                          {file.name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {file.isDirectory
                            ? "Folder"
                            : file.size !== undefined
                              ? `${(file.size / 1024).toFixed(1)} KB`
                              : "File"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            /* File Preview — taller for PDFs to maximize viewer space */
            <div
              className={cn(
                "overflow-hidden rounded-lg border",
                isTallPreview ? "h-[70vh]" : "h-full sm:h-[500px]",
              )}
            >
              <div className="border-b bg-muted px-4 py-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">
                    {selectedFile
                      ? selectedFile.split("/").pop()
                      : "File Preview"}
                  </span>
                  {selectedFile && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleBackToList}
                      className="h-7 px-2"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
              <div
                className={cn(
                  "h-[calc(100%-44px)]",
                  isFullBleedPreview ? "overflow-hidden p-0" : "overflow-y-auto p-4",
                )}
              >
                {isLoading && selectedFile ? (
                  <div className="space-y-2 p-4">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                      <Skeleton key={i} className="h-4 w-full" />
                    ))}
                  </div>
                ) : selectedPreviewSource ? (
                  <FilePreviewBody source={selectedPreviewSource} />
                ) : fileContent === "__sheet__preview__" && sheetPreview ? (
                  <WorkbookSpreadsheetPreview sheets={sheetPreview} />
                ) : fileContent ? (
                  <pre className="whitespace-pre-wrap text-xs font-mono leading-relaxed">
                    {fileContent}
                  </pre>
                ) : (
                  <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
                    <File className="mb-4 h-12 w-12 opacity-50" />
                    <p className="text-lg font-medium">Select a file to preview</p>
                    <p className="text-sm">Click on any file to view its contents</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-4 px-4 py-3 flex-shrink-0">
          <div className="text-xs text-muted-foreground">
            {!isWorkspaceReady
              ? ""
              : selectedFile && fileContent && onSelectFile && viewMode === "preview"
                ? "Viewing file content"
                : viewMode === "list"
                  ? "Click on files to preview or folders to navigate"
                  : "Viewing file content"}
          </div>

          {isWorkspaceReady && (
            <div className="flex items-center gap-2">
              {selectedFile && fileContent && onSelectFile && viewMode === "preview" ? (
                <>
                  <Button variant="outline" size="sm" onClick={handleBackToList}>
                    Back to Files
                  </Button>
                  <Button size="sm" onClick={handleSelectFile}>
                    Select File
                  </Button>
                </>
              ) : (
                <>
                  {fileSource !== "snapshot" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUpload}
                      disabled={isUploading || viewMode === "preview"}
                      className="h-9"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {isUploading ? "Uploading..." : "Upload Files"}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownload}
                    disabled={!selectedFile}
                    className="h-9"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
